import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import evaluatorImg   from "./images/evaluator.svg";
import decisionImg    from "./images/decision.svg";
import explanationImg from "./images/explanation.svg";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  bg:      "#08101e",
  bg1:     "#0c1525",
  bg2:     "#101b2e",
  bg3:     "#141f33",
  border:  "#1e2d42",
  border2: "#243548",
  text:    "#e8edf5",
  body:    "#b8c8dc",
  meta:    "#6a7d95",
  dim:     "#2d3f58",
  green:   "#00e896",
  cyan:    "#00c8ff",
  amber:   "#ffb340",
  red:     "#ff4466",
  blue:    "#4488ff",
  purple:  "#9966ff",
  gDim:    "rgba(0,232,150,0.07)",
  cDim:    "rgba(0,200,255,0.07)",
  aDim:    "rgba(255,179,64,0.07)",
  pDim:    "rgba(153,102,255,0.07)",
};

// ─────────────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────────────
const LAST_SESSION = {
  date:    "Feb 26",
  trades:  10,
  winners: 8,
  losers:  2,
  pnl:     "+$1,658",
  rate:    80,
};

const CUMULATIVE = {
  totalReturn:  "+$14,280",
  sessions:     47,
  startDate:    "Nov 2024",
};

const DAILY = [
  {
    date:"Feb 26", trades:10, winners:8, losers:2,
    pnl:"+$1,658", rate:80, roi:83,
    scenario:"TRENDING",
    scenarioColor:"#00e896",
    scenarioNote:"Strong directional day · bot found 8 high-confidence setups · all stops held on losses",
  },
  {
    date:"Feb 25", trades:1, winners:1, losers:0,
    pnl:"+$20", rate:100, roi:1,
    scenario:"SELECTIVE",
    scenarioColor:"#00c8ff",
    scenarioNote:"Choppy regime · bot evaluated 9 setups · skipped 8 · 1 high-conviction entry only",
  },
  {
    date:"Feb 24", trades:9, winners:5, losers:4,
    pnl:"-$140", rate:56, roi:-7,
    scenario:"DEFENSIVE",
    scenarioColor:"#ff4466",
    scenarioNote:"Volatile open · 4 stops triggered as designed · max loss per trade enforced · no runaway positions",
  },
  {
    date:"Feb 23", trades:12, winners:10, losers:2,
    pnl:"+$720", rate:83, roi:36,
    scenario:"RECOVERY",
    scenarioColor:"#9966ff",
    scenarioNote:"Session after defensive day · no behavioral change · bot executed normal scan · 83% win rate",
  },
];

const SAMPLE_TRADE = [
  { time:"10:06:25", event:"PROCEED",  detail:"SPY PUT $691 · 10 contracts · $2.05",    color:"#00c8ff", icon:"▶" },
  { time:"10:07:45", event:"HOLD",     detail:"Momentum building · +$0.12 above entry", color:"#ffb340", icon:"◈" },
  { time:"10:08:10", event:"CLOSED",   detail:"Target hit · $2.17 · +$120 · 1m 19s",   color:"#00e896", icon:"■" },
];

const SEGMENTS = [
  { icon:"◎", color:C.cyan,
    title:"Passive Investors",
    hook:"Earn from markets without trading yourself.",
    desc:"The bot deploys a fixed capital amount every morning and closes everything by 4pm. No skill required, no daily monitoring, no overnight exposure. Full transparency into every decision made on your behalf.",
    proof:"Fixed capital · recycled daily · no overnight risk",
  },
  { icon:"⬡", color:C.purple,
    title:"Retail Traders",
    hook:"You've traded options. You've lost more than you've won.",
    desc:"You had a system — it just wasn't consistent. BerBul gives you institutional-grade signal quality with a complete audit trail on every trade. See exactly why each position was entered, monitored, and closed.",
    proof:"Full evaluation timeline · every trade explained",
  },
  { icon:"◈", color:C.green,
    title:"Professional Traders",
    hook:"Use the bot's signal as your research layer.",
    desc:"The bot surfaces the edge. You decide how aggressively to size it. Customisable risk parameters, manual position expansion, and an analysis view with full regime classification and RL confidence scoring.",
    proof:"Customisable risk params · manual override · analysis mode",
  },
];

const TICKS = [
  { label:"WIN RATE",          val:"78%",          color:C.green  },
  { label:"AVG HOLD TIME",     val:"97 seconds",   color:C.cyan   },
  { label:"AVG DAILY P&L",     val:"+$487",        color:C.green  },
  { label:"MAX LIVE EXPOSURE", val:"< $4,000",     color:C.amber  },
  { label:"INSTRUMENT",        val:"SPY 0DTE",     color:C.cyan   },
  { label:"STRATEGY",          val:"SCALP",        color:C.purple },
  { label:"PROFITABLE DAYS",   val:"18 of 22",     color:C.green  },
  { label:"OVERNIGHT RISK",    val:"ZERO",         color:C.red    },
  { label:"AI LAYERS",         val:"5 — AGENTIC",  color:C.blue   },
  { label:"TOTAL RETURN",      val:"+$14,280",     color:C.green  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MARKET HOURS HELPER
// ─────────────────────────────────────────────────────────────────────────────
function getMarketStatus() {
  const now = new Date();
  const et  = new Date(now.toLocaleString("en-US", { timeZone:"America/New_York" }));
  const day = et.getDay();
  const h   = et.getHours();
  const m   = et.getMinutes();
  const mins = h * 60 + m;
  const isWeekday = day >= 1 && day <= 5;
  const isOpen    = isWeekday && mins >= 570 && mins < 960;
  const timeStr   = et.toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", timeZoneName:"short" });
  return { isOpen, timeStr, et };
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────
function useInView(ref, threshold = 0.08) {
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } },
      { threshold }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return v;
}

function useCountUp(end, active, ms = 1800) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const num = parseFloat(String(end).replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return;
    let s = null;
    const tick = t => {
      if (!s) s = t;
      const p = Math.min((t - s) / ms, 1);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * num));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, end]);
  return n;
}

// ─────────────────────────────────────────────────────────────────────────────
// MICRO COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function Orb({ color, size = 7, speed = "3s" }) {
  return (
    <span style={{ position:"relative", display:"inline-block",
      width:size, height:size, flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, borderRadius:"50%",
        background:color, opacity:.12,
        animation:`orbPulse ${speed} ease-in-out infinite` }} />
      <span style={{ position:"absolute", inset:Math.round(size * .28),
        borderRadius:"50%", background:color }} />
    </span>
  );
}

function Chip({ children, color = C.cyan }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center",
      padding:"3px 10px", borderRadius:3, fontSize:9,
      fontFamily:"'DM Mono',monospace", fontWeight:500,
      letterSpacing:".8px", whiteSpace:"nowrap",
      background:color + "12", border:`1px solid ${color}28`, color }}>
      {children}
    </span>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
      color:C.meta, letterSpacing:"2px", marginBottom:16,
      textTransform:"uppercase" }}>
      {children}
    </div>
  );
}

function Heading({ children, style = {} }) {
  return (
    <h2 style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
      fontSize:"clamp(22px,2.8vw,34px)", letterSpacing:"-.3px",
      lineHeight:1.22, color:C.text, margin:"0 0 20px", ...style }}>
      {children}
    </h2>
  );
}

function Body({ children, style = {} }) {
  return (
    <p style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
      color:C.body, lineHeight:1.92, fontWeight:300, margin:0, ...style }}>
      {children}
    </p>
  );
}

function Divider() {
  return <div style={{ height:1, background:C.border, margin:"110px 0" }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// MARKET STATUS BAR
// ─────────────────────────────────────────────────────────────────────────────
function MarketBar() {
  const [status, setStatus] = useState(getMarketStatus());
  useEffect(() => {
    const t = setInterval(() => setStatus(getMarketStatus()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ background:C.bg1, borderBottom:`1px solid ${C.border}`,
      padding:"8px 52px", display:"flex", alignItems:"center",
      justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <Orb color={status.isOpen ? C.green : C.meta} size={7}
          speed={status.isOpen ? "2s" : "4s"} />
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:status.isOpen ? C.green : C.meta, letterSpacing:"1px",
          fontWeight:500 }}>
          {status.isOpen ? "MARKET OPEN" : "MARKET CLOSED"}
        </span>
        <span style={{ color:C.dim, fontSize:9 }}>·</span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.meta, letterSpacing:".5px" }}>{status.timeStr}</span>
        {status.isOpen && (
          <>
            <span style={{ color:C.dim, fontSize:9 }}>·</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
              color:C.body, letterSpacing:".5px" }}>
              Bot active · SPY monitoring
            </span>
          </>
        )}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.meta, letterSpacing:".5px" }}>
          Example session {LAST_SESSION.date}
        </span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.green, fontWeight:500 }}>{LAST_SESSION.pnl}</span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.body }}>{LAST_SESSION.trades} trades · {LAST_SESSION.rate}% win rate</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TICKER
// ─────────────────────────────────────────────────────────────────────────────
function Ticker() {
  const all = [...TICKS, ...TICKS];
  return (
    <div style={{ overflow:"hidden", background:C.bg,
      borderTop:`1px solid ${C.border}`,
      borderBottom:`1px solid ${C.border}`,
      position:"relative" }}>
      {["left","right"].map(s => (
        <div key={s} style={{ position:"absolute", [s]:0, top:0, bottom:0, width:80,
          background:`linear-gradient(${s === "left" ? "90" : "270"}deg, ${C.bg}, transparent)`,
          zIndex:2, pointerEvents:"none" }} />
      ))}
      <div style={{ display:"flex", width:"max-content",
        animation:"ticker 44s linear infinite" }}>
        {all.map((t, i) => (
          <div key={i} style={{ display:"flex", flexDirection:"column",
            justifyContent:"center", gap:5,
            padding:"14px 32px",
            borderRight:`1px solid ${C.border}`,
            minWidth:152 }}>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:C.meta, letterSpacing:"1.2px", lineHeight:1 }}>
              {t.label}
            </span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:16,
              fontWeight:500, color:t.color, letterSpacing:".2px", lineHeight:1 }}>
              {t.val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRADE REPLAY
// ─────────────────────────────────────────────────────────────────────────────
function TradeReplay() {
  const ref = useRef();
  const vis = useInView(ref);
  const [step, setStep] = useState(-1);

  useEffect(() => {
    if (!vis) return;
    const timers = SAMPLE_TRADE.map((_, i) =>
      setTimeout(() => setStep(i), i * 900 + 400)
    );
    return () => timers.forEach(clearTimeout);
  }, [vis]);

  return (
    <div ref={ref} style={{
      background:C.bg1, border:`1px solid ${C.border}`,
      borderRadius:14, overflow:"hidden", maxWidth:640, margin:"0 auto",
      opacity:vis ? 1 : 0,
      animation:vis ? "fadeUp .7s ease both" : "none",
    }}>
      <div style={{ padding:"14px 24px", borderBottom:`1px solid ${C.border}`,
        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
          <Orb color={C.green} size={7} speed="2s" />
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
            color:C.body, letterSpacing:"1px" }}>
            EXAMPLE TRADE — FEB 26 · SPY 0DTE · PAPER ACCOUNT
          </span>
        </div>
        <Chip color={C.green}>+$120 · 1m 19s</Chip>
      </div>
      <div style={{ padding:"8px 0" }}>
        {SAMPLE_TRADE.map((t, i) => (
          <div key={i} style={{
            display:"flex", alignItems:"flex-start", gap:16,
            padding:"16px 24px",
            borderBottom:i < SAMPLE_TRADE.length - 1
              ? `1px solid ${C.border}` : "none",
            opacity:step >= i ? 1 : 0,
            transform:step >= i ? "translateY(0)" : "translateY(8px)",
            transition:"opacity .4s ease, transform .4s ease",
          }}>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
              color:C.meta, whiteSpace:"nowrap", paddingTop:2,
              minWidth:60 }}>{t.time}</span>
            <div style={{ display:"flex", alignItems:"center", gap:8,
              minWidth:80 }}>
              <span style={{ fontSize:12, color:t.color }}>{t.icon}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
                color:t.color, fontWeight:500, letterSpacing:".8px" }}>
                {t.event}
              </span>
            </div>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
              color:C.body, lineHeight:1.6, fontWeight:300 }}>
              {t.detail}
            </span>
          </div>
        ))}
      </div>
      <div style={{ padding:"12px 24px", background:C.bg,
        borderTop:`1px solid ${C.border}`,
        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.meta, letterSpacing:".4px" }}>
          One of 10 trades on Feb 26
        </span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.body, letterSpacing:".4px" }}>
          Full audit trail available after login
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ num, label, sub, color, prefix = "", suffix = "", delay = 0 }) {
  const ref = useRef();
  const vis = useInView(ref);
  const raw = parseFloat(String(num).replace(/[^0-9.]/g, ""));
  const counted = useCountUp(raw, vis);
  const isNum = !isNaN(raw);
  const display = isNum ? `${prefix}${counted.toLocaleString()}${suffix}` : num;

  return (
    <div ref={ref} style={{
      background:C.bg1, border:`1px solid ${C.border}`,
      borderRadius:12, padding:"32px 28px",
      opacity:vis ? 1 : 0,
      animation:vis ? `fadeUp .6s ${delay}s ease both` : "none",
    }}>
      <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
        fontSize:"clamp(26px,2.8vw,40px)", color:color || C.text,
        letterSpacing:"-.5px", lineHeight:1, marginBottom:14 }}>
        {display}
      </div>
      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
        color:C.meta, letterSpacing:"1.2px", marginBottom:sub ? 7 : 0,
        textTransform:"uppercase" }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
          color:C.body, letterSpacing:".2px" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CAPITAL CYCLE
// ─────────────────────────────────────────────────────────────────────────────
function CapitalCycle() {
  const ref = useRef();
  const vis = useInView(ref);
  const nodes = [
    { t:"09:30 AM", label:"Capital deployed",     val:"Fixed amount", color:C.cyan,   icon:"▶" },
    { t:"SESSION",  label:"10–12 trades",          val:"97s avg hold", color:C.green,  icon:"⬡" },
    { t:"04:00 PM", label:"All positions closed",  val:"+$487 avg",    color:C.purple, icon:"■" },
    { t:"NEXT DAY", label:"Same capital",          val:"back to work", color:C.amber,  icon:"↺" },
  ];
  return (
    <div ref={ref} style={{ display:"flex", alignItems:"center",
      justifyContent:"center", flexWrap:"wrap", gap:0, margin:"52px 0 40px" }}>
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          <div style={{ background:C.bg1, border:`1px solid ${n.color}20`,
            borderRadius:12, padding:"28px 28px", textAlign:"center", minWidth:152,
            opacity:vis ? 1 : 0,
            animation:vis ? `fadeUp .6s ${i * .12}s ease both` : "none" }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:C.meta, letterSpacing:"1.2px", marginBottom:14 }}>{n.t}</div>
            <div style={{ fontSize:20, color:n.color, marginBottom:10 }}>{n.icon}</div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
              color:C.body, marginBottom:6 }}>{n.label}</div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
              fontSize:14, color:n.color }}>{n.val}</div>
          </div>
          {i < nodes.length - 1 && (
            <div style={{ padding:"0 14px", color:C.dim, fontSize:16,
              opacity:vis ? 1 : 0,
              animation:vis ? `fadeUp .4s ${i * .12 + .06}s ease both` : "none" }}>
              →
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PERFORMANCE TABLE
// ─────────────────────────────────────────────────────────────────────────────
function PerfTable() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref} style={{
      opacity:vis ? 1 : 0,
      animation:vis ? "fadeUp .7s ease both" : "none",
    }}>
      <div style={{
        display:"flex", alignItems:"flex-start", gap:14,
        padding:"16px 20px", marginBottom:10,
        background:"rgba(255,179,64,0.06)",
        border:`1px solid ${C.amber}28`,
        borderRadius:10,
      }}>
        <span style={{ fontSize:14, color:C.amber, flexShrink:0, paddingTop:1 }}>⊙</span>
        <div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
            color:C.amber, letterSpacing:"1.2px", fontWeight:500, marginBottom:5 }}>
            CURATED EXAMPLE · PAPER TRADING · NOV 2024–FEB 2026
          </div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
            color:C.body, lineHeight:1.7, fontWeight:300 }}>
            Four real paper-trading sessions selected to show how the system behaves
            across different market conditions — including a loss day and a quiet day.
            Not filtered for wins. Full session history available after login.
          </div>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        {[
          { label:"TRENDING",  color:C.green,  desc:"High-conviction day" },
          { label:"SELECTIVE", color:C.cyan,   desc:"Bot traded sparingly" },
          { label:"DEFENSIVE", color:C.red,    desc:"Loss day · stops held" },
          { label:"RECOVERY",  color:C.purple, desc:"Session after loss" },
        ].map(s => (
          <div key={s.label} style={{
            display:"flex", alignItems:"center", gap:7,
            padding:"5px 11px",
            background:s.color + "0e",
            border:`1px solid ${s.color}28`,
            borderRadius:4,
          }}>
            <span style={{ width:5, height:5, borderRadius:"50%",
              background:s.color, flexShrink:0 }} />
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:s.color, letterSpacing:".8px", fontWeight:500 }}>{s.label}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:C.meta }}>· {s.desc}</span>
          </div>
        ))}
      </div>
      <div style={{
        background:C.bg1, border:`1px solid ${C.border}`,
        borderRadius:14, overflow:"hidden",
      }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse",
            fontFamily:"'DM Mono',monospace" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                {["DATE","SESSION TYPE","TRADES","W / L","NET P&L","WIN RATE","SESSION ROI"].map(h => (
                  <th key={h} style={{ padding:"12px 20px", fontSize:9,
                    color:C.meta, letterSpacing:"1.1px", textAlign:"left",
                    fontWeight:400, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAILY.map((d, i) => {
                const isLoss = d.pnl.startsWith("-");
                const pnlColor = isLoss ? C.red : C.green;
                const roiColor = isLoss ? C.red : C.purple;
                return (
                  <tr key={i}
                    style={{
                      borderBottom:i < DAILY.length - 1 ? `1px solid ${C.bg2}` : "none",
                      transition:"background .15s", cursor:"default",
                      borderLeft:`2px solid ${d.scenarioColor}40`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding:"16px 20px", color:C.body,
                      fontSize:11, whiteSpace:"nowrap" }}>{d.date}</td>
                    <td style={{ padding:"16px 20px", minWidth:220 }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                        <span style={{
                          display:"inline-block", padding:"2px 8px", borderRadius:3,
                          fontSize:8, fontWeight:500, letterSpacing:".8px",
                          color:d.scenarioColor,
                          background:d.scenarioColor + "12",
                          border:`1px solid ${d.scenarioColor}30`,
                          width:"fit-content",
                        }}>{d.scenario}</span>
                        <span style={{ fontSize:9, color:C.meta,
                          lineHeight:1.5, fontWeight:300 }}>
                          {d.scenarioNote}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding:"16px 20px", color:C.text,
                      fontSize:11 }}>{d.trades}</td>
                    <td style={{ padding:"16px 20px", fontSize:11 }}>
                      <span style={{ color:C.green }}>{d.winners}</span>
                      <span style={{ color:C.meta }}> / </span>
                      <span style={{ color:d.losers > 0 ? C.red : C.meta }}>
                        {d.losers}
                      </span>
                    </td>
                    <td style={{ padding:"16px 20px", color:pnlColor,
                      fontWeight:500, fontSize:15 }}>{d.pnl}</td>
                    <td style={{ padding:"16px 20px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:48, height:2, background:C.bg3,
                          borderRadius:1, flexShrink:0 }}>
                          <div style={{ height:"100%", borderRadius:1,
                            background:d.rate >= 70 ? C.green : d.rate >= 55 ? C.amber : C.red,
                            width:`${d.rate}%` }} />
                        </div>
                        <span style={{
                          color:d.rate >= 70 ? C.green : d.rate >= 55 ? C.amber : C.red,
                          fontSize:11, fontWeight:500,
                        }}>{d.rate}%</span>
                      </div>
                    </td>
                    <td style={{ padding:"16px 20px", color:roiColor,
                      fontWeight:500, fontSize:13 }}>
                      {isLoss ? "" : "+"}{d.roi}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop:`1px solid ${C.border}`, background:C.bg }}>
                <td colSpan={7} style={{ padding:"12px 20px", fontSize:9,
                  color:C.meta, letterSpacing:".4px", lineHeight:1.7 }}>
                  ∗ Paper account · SPY 0DTE options · curated example sessions ·
                  past performance does not guarantee future results
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL CAPTURE
// ─────────────────────────────────────────────────────────────────────────────
function EmailCapture() {
  const [email, setEmail] = useState("");
  const [sent,  setSent]  = useState(false);
  const submit = () => {
    if (!email.includes("@")) return;
    setSent(true);
  };
  return (
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`,
      borderRadius:12, padding:"36px 36px", textAlign:"center" }}>
      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
        color:C.meta, letterSpacing:"2px", marginBottom:14 }}>
        DAILY SESSION ALERTS
      </div>
      <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
        fontSize:18, color:C.text, marginBottom:10 }}>
        Watch it trade live — every day.
      </div>
      <p style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
        color:C.body, lineHeight:1.85, fontWeight:300,
        maxWidth:400, margin:"0 auto 28px" }}>
        Get a link at 9:25am ET every trading day.
        Watch the bot run its session live — no account required.
      </p>
      {sent ? (
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
          color:C.green, letterSpacing:".5px" }}>
          ✓ You're in. Watch for a link tomorrow morning.
        </div>
      ) : (
        <div style={{ display:"flex", gap:0, maxWidth:400, margin:"0 auto",
          border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden" }}>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submit()}
            style={{ flex:1, padding:"12px 16px",
              background:C.bg, border:"none", outline:"none",
              fontFamily:"'DM Mono',monospace", fontSize:11,
              color:C.text, letterSpacing:".3px" }}
          />
          <button onClick={submit} style={{
            padding:"12px 20px", background:C.cyan, border:"none",
            fontFamily:"'DM Mono',monospace", fontSize:10,
            color:"#040810", fontWeight:500, letterSpacing:".5px",
            cursor:"pointer", transition:"opacity .2s", whiteSpace:"nowrap"
          }}
            onMouseEnter={e => e.target.style.opacity = ".84"}
            onMouseLeave={e => e.target.style.opacity = "1"}
          >
            Notify Me →
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED CSS
// ─────────────────────────────────────────────────────────────────────────────
const sharedCSS = (C) => `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  .bb-root::before {
    content:'';
    position:fixed; inset:0; z-index:0; pointer-events:none;
    background-image:
      linear-gradient(${C.border}22 1px, transparent 1px),
      linear-gradient(90deg, ${C.border}22 1px, transparent 1px);
    background-size:64px 64px;
  }
  @keyframes orbPulse {
    0%,100% { transform:scale(1); opacity:.12; }
    50%      { transform:scale(3.2); opacity:0; }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(16px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes ticker {
    from { transform:translateX(0); }
    to   { transform:translateX(-50%); }
  }
  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-track { background:${C.bg}; }
  ::-webkit-scrollbar-thumb { background:${C.border2}; border-radius:2px; }
  input::placeholder { color:${C.meta}; }
  .bb-nav {
    position:fixed; top:0; left:0; right:0; z-index:200;
    display:flex; align-items:center; justify-content:space-between;
    padding:14px 52px;
    background:rgba(8,16,30,0.96);
    backdrop-filter:blur(22px);
    border-bottom:1px solid ${C.border};
  }
  .bb-logo {
    display:flex; align-items:center; gap:11px;
    font-family:'DM Mono',monospace; font-weight:500;
    font-size:14px; color:${C.text}; text-decoration:none;
    letter-spacing:.3px;
  }
  .bb-mark {
    width:30px; height:30px; border-radius:6px;
    border:1px solid ${C.cyan}40; background:${C.cDim};
    display:grid; place-items:center;
    font-family:'DM Mono',monospace; font-size:12px; color:${C.cyan};
  }
  .bb-nav-links { display:flex; gap:32px; list-style:none; }
  .bb-nav-links a {
    font-family:'DM Mono',monospace; font-size:9px;
    color:${C.meta}; text-decoration:none;
    letter-spacing:1.2px; text-transform:uppercase; transition:color .2s;
  }
  .bb-nav-links a:hover { color:${C.text}; }
  .bb-btn-ghost {
    padding:9px 18px; border-radius:5px;
    border:1px solid ${C.border2}; color:${C.body};
    font-family:'DM Mono',monospace; font-size:9px; font-weight:500;
    letter-spacing:.6px; text-decoration:none;
    transition:border-color .2s, color .2s;
    background:transparent; margin-right:8px;
  }
  .bb-btn-ghost:hover { border-color:${C.cyan}; color:${C.cyan}; }
  .bb-btn-solid {
    padding:9px 18px; border-radius:5px;
    background:${C.cyan}; color:#040810;
    font-family:'DM Mono',monospace; font-size:9px; font-weight:500;
    letter-spacing:.6px; text-decoration:none; transition:opacity .2s;
  }
  .bb-btn-solid:hover { opacity:.84; }
  .bb-page {
    position:relative; z-index:1;
    max-width:1080px; margin:0 auto; padding:0 32px 100px;
  }
  .bb-hero {
    padding:100px 0 90px;
    text-align:center; position:relative;
  }
  .bb-glow {
    position:absolute; top:-80px; left:50%;
    transform:translateX(-50%);
    width:680px; height:400px; border-radius:50%;
    background:radial-gradient(ellipse,
      rgba(0,200,255,0.032) 0%, transparent 65%);
    pointer-events:none;
  }
  .bb-badge {
    display:inline-flex; align-items:center; gap:9px;
    padding:6px 16px; border-radius:999px;
    border:1px solid ${C.cyan}22; background:${C.cDim};
    font-family:'DM Mono',monospace; font-size:9px;
    color:${C.cyan}; letter-spacing:1.2px; margin-bottom:32px;
  }
  .bb-h1 {
    font-family:'DM Mono',monospace; font-weight:500;
    font-size:clamp(28px,4.8vw,56px);
    line-height:1.2; letter-spacing:-.5px; margin-bottom:12px;
    color:${C.text};
  }
  .bb-h1-hook {
    display:block; font-size:clamp(14px,2vw,22px);
    color:${C.meta}; font-weight:300; letter-spacing:".2px";
    margin-bottom:8px;
  }
  .bb-h1-num {
    display:block; font-size:clamp(52px,8vw,96px);
    color:${C.cyan}; font-weight:500; letter-spacing:-3px;
    line-height:.95;
  }
  .bb-h1-frame {
    display:block; font-size:clamp(14px,1.8vw,20px);
    color:${C.body}; font-weight:300; margin-top:10px;
    letter-spacing:".1px";
  }
  .bb-sub {
    max-width:540px; margin:28px auto 44px;
    font-family:'DM Mono',monospace;
    font-size:12px; line-height:1.95;
    color:${C.body}; font-weight:300;
  }
  .bb-sub strong { color:${C.text}; font-weight:500; }
  .bb-cta { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
  .bb-bm {
    display:inline-flex; align-items:center; gap:8px;
    padding:14px 32px; border-radius:6px;
    background:${C.cyan}; color:#040810;
    font-family:'DM Mono',monospace; font-size:11px;
    font-weight:500; letter-spacing:.5px;
    text-decoration:none; border:none; cursor:pointer;
    transition:opacity .2s, transform .2s;
  }
  .bb-bm:hover { opacity:.84; transform:translateY(-1px); }
  .bb-bg {
    display:inline-flex; align-items:center; gap:8px;
    padding:14px 32px; border-radius:6px;
    border:1px solid ${C.border2}; color:${C.body};
    font-family:'DM Mono',monospace; font-size:11px;
    font-weight:400; letter-spacing:.4px; text-decoration:none;
    transition:border-color .2s, color .2s; background:transparent;
  }
  .bb-bg:hover { border-color:${C.cyan}; color:${C.cyan}; }
  .bb-caveat {
    font-family:'DM Mono',monospace; font-size:9px;
    color:${C.meta}; text-align:center; margin-top:16px;
    letter-spacing:".3px"; line-height:1.7;
  }
  .bb-stats {
    display:grid; grid-template-columns:repeat(5,1fr);
    gap:12px; margin:72px 0 24px;
  }
  .bb-shead { margin-bottom:48px; }
  .bb-segs { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
  .bb-seg {
    background:${C.bg1}; border:1px solid ${C.border};
    border-radius:12px; padding:36px 28px;
    display:flex; flex-direction:column; gap:14px;
    transition:border-color .22s;
  }
  .bb-seg:hover { border-color:${C.border2}; }
  .bb-pdt {
    margin-top:32px; background:${C.aDim};
    border:1px solid ${C.amber}1e; border-radius:12px; padding:28px 32px;
  }
  .bb-pdt-grid {
    display:grid; grid-template-columns:repeat(3,1fr);
    gap:16px; margin-top:20px;
  }
  .bb-pdt-cell {
    background:${C.bg}; border:1px solid ${C.border};
    border-radius:8px; padding:18px 20px;
  }
  .bb-cumul {
    background:${C.gDim}; border:1px solid ${C.green}1e;
    border-radius:12px;
    display:flex; align-items:center; justify-content:center;
    gap:48px; flex-wrap:wrap; margin-top:32px; padding:28px 32px;
  }
  .bb-bcta {
    text-align:center; padding:100px 32px;
    border-top:1px solid ${C.border};
    border-bottom:1px solid ${C.border};
    margin-bottom:72px; position:relative; overflow:hidden;
  }
  .bb-bcta::before {
    content:''; position:absolute; top:0; left:50%;
    transform:translateX(-50%);
    width:600px; height:280px; border-radius:50%;
    background:radial-gradient(ellipse,
      rgba(0,200,255,0.025) 0%, transparent 70%);
    pointer-events:none;
  }
  .bb-bcta h2 {
    font-family:'DM Mono',monospace; font-weight:500;
    font-size:clamp(24px,3.2vw,42px);
    letter-spacing:"-.3px"; margin-bottom:16px; color:${C.text};
  }
  .bb-bcta p {
    font-family:'DM Mono',monospace; font-size:12px;
    color:${C.body}; margin:0 auto 40px; max-width:440px;
    line-height:1.92; font-weight:300;
  }
  .bb-demo {
    display:inline-flex; align-items:center; gap:14px;
    padding:14px 24px; flex-wrap:wrap;
    background:${C.cDim}; border:1px solid ${C.cyan}18;
    border-radius:8px; font-family:'DM Mono',monospace;
    font-size:10px; color:${C.body}; margin-top:20px;
  }
  .bb-risk {
    background:${C.bg1}; border:1px solid ${C.border};
    border-radius:10px; padding:24px 28px; margin-bottom:56px;
  }
  .bb-footer {
    display:flex; justify-content:space-between; align-items:center;
    padding:28px 0; border-top:1px solid ${C.border};
    font-family:'DM Mono',monospace; font-size:9px; color:${C.meta};
    flex-wrap:wrap; gap:12px; letter-spacing:".6px";
  }
  .bb-footer-logo {
    font-family:'DM Mono',monospace; font-weight:500;
    font-size:14px; color:${C.text};
  }
  @media(max-width:900px){
    .bb-nav { padding:14px 20px; }
    .bb-nav-links { display:none; }
    .bb-stats { grid-template-columns:repeat(2,1fr); }
    .bb-segs  { grid-template-columns:1fr; }
    .bb-pdt-grid { grid-template-columns:1fr; }
    .bb-page { padding:0 20px 72px; }
    .bb-hero { padding:80px 0 72px; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// THESIS SECTION — shared between both pages
// ─────────────────────────────────────────────────────────────────────────────
function ThesisSection() {
  return (
    <section style={{
      padding:"80px 0 88px",
      borderTop:`1px solid ${C.border}`,
      borderBottom:`1px solid ${C.border}`,
    }}>
      <div style={{ maxWidth:960, margin:"0 auto", padding:"0 40px" }}>
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <h2 style={{
            fontFamily:"'DM Mono',monospace",
            fontSize:"clamp(26px,4vw,40px)",
            fontWeight:700, lineHeight:1.15,
            letterSpacing:"-0.02em", margin:"0 0 6px",
          }}>
            <span style={{ color:C.body }}>You don't trade on BerBul.</span><br/>
            <span style={{ color:C.green }}>BerBul trades for you.</span>
          </h2>
          <p style={{
            fontFamily:"'DM Mono',monospace",
            fontSize:14, color:C.meta,
            lineHeight:1.95, fontWeight:300,
            maxWidth:560, margin:"18px auto 0",
          }}>
            Your only job is the capital.{" "}
            <span style={{ color:C.body, fontWeight:500 }}>Fund it. Walk away.</span>
            {" "}Come back to a session log that's been right more than it's been wrong.
          </p>
        </div>
        <div style={{
          display:"grid",
          gridTemplateColumns:"repeat(3,minmax(0,1fr))",
          border:`1px solid ${C.border}`,
          borderRadius:10,
          overflow:"hidden",
        }}>
          {[
            {
              num:"01", label:"the problem",
              title:"Human decisions destroy consistent edge",
              body:"Fear, hope, and a bad night's sleep make you trade the same setup differently every time. No amount of discipline fixes that reliably.",
              tag:"The human variable",
              tagBg:"rgba(255,179,64,.1)", tagColor:C.amber, tagBorder:"rgba(255,179,64,.2)",
              border:false,
            },
            {
              num:"02", label:"the shift",
              title:"You are the owner. Not the operator.",
              body:"Set capital. The agent handles every entry, every monitoring check, every exit. Your job ends the moment you fund the session.",
              tag:"Fully automated",
              tagBg:"rgba(0,200,255,.1)", tagColor:C.cyan, tagBorder:"rgba(0,200,255,.2)",
              border:true,
            },
            {
              num:"03", label:"the result",
              title:"Capital working every trading day",
              body:"In and out before 4pm. P&L realized. Capital recycled. Every decision logged in plain English. No overnight exposure. No input needed.",
              tag:"Money at work",
              tagBg:"rgba(0,232,150,.1)", tagColor:C.green, tagBorder:"rgba(0,232,150,.2)",
              border:false,
            },
          ].map((b) => (
            <div key={b.num} style={{
              background:C.bg1, padding:"26px 22px 24px",
              display:"flex", flexDirection:"column", gap:10,
              borderLeft: b.border ? `1px solid ${C.border}` : "none",
              borderRight: b.border ? `1px solid ${C.border}` : "none",
            }}>
              <div style={{
                fontFamily:"'DM Mono',monospace",
                fontSize:10, letterSpacing:".14em",
                color:"#8aa0b8", textTransform:"uppercase", fontWeight:500,
              }}>{b.num} — {b.label}</div>
              <div style={{
                fontFamily:"'DM Mono',monospace",
                fontSize:12, fontWeight:600, color:C.text, lineHeight:1.45,
              }}>{b.title}</div>
              <div style={{
                fontFamily:"'DM Mono',monospace",
                fontSize:11, color:C.meta, lineHeight:1.8, fontWeight:300, flex:1,
              }}>{b.body}</div>
              <span style={{
                display:"inline-block", fontFamily:"'DM Mono',monospace",
                fontSize:9, letterSpacing:".1em", padding:"3px 9px",
                borderRadius:4, fontWeight:500, alignSelf:"flex-start",
                background:b.tagBg, color:b.tagColor,
                border:`1px solid ${b.tagBorder}`,
              }}>{b.tag}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop:28, display:"flex", alignItems:"center", gap:8 }}>
          <Link to="/approach" style={{
            fontFamily:"'DM Mono',monospace",
            fontSize:11, color:C.cyan,
            textDecoration:"none", fontWeight:500, letterSpacing:".03em",
          }}>Read the full approach →</Link>
          <span style={{ fontSize:9, color:C.dim }}>·</span>
          <span style={{
            fontFamily:"'DM Mono',monospace",
            fontSize:10, color:C.dim, letterSpacing:".04em",
          }}>No account required · paper trading by default</span>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED NAV + SHELL
// ─────────────────────────────────────────────────────────────────────────────
function Shell({ mkt, navLinks, ctaTo, ctaLabel, children }) {
  return (
    <div style={{ background:C.bg, color:C.text, minHeight:"100vh",
      overflowX:"hidden", fontFamily:"'DM Mono',monospace" }}>
      <style>{sharedCSS(C)}</style>

      <nav className="bb-nav">
        <a className="bb-logo" href="/">
          <div className="bb-mark">B</div>
          BerBul AI Trading
        </a>
        <ul className="bb-nav-links">
          {navLinks.map(({ href, label }) => (
            <li key={href}><a href={href}>{label}</a></li>
          ))}
        </ul>
        <div style={{ display:"flex", alignItems:"center" }}>
          <Link className="bb-btn-ghost" to="/Login">Sign In</Link>
          <Link className="bb-btn-solid" to={ctaTo}>{ctaLabel}</Link>
        </div>
      </nav>

      <div style={{ paddingTop:57 }}>
        <MarketBar />
      </div>
      <Ticker />

      <div className="bb-page bb-root">
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RISK DISCLOSURE — shared
// ─────────────────────────────────────────────────────────────────────────────
function RiskDisclosure() {
  return (
    <div className="bb-risk">
      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
        color:C.meta, letterSpacing:"1.2px", marginBottom:10 }}>
        RISK DISCLOSURE
      </div>
      <p style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
        color:C.meta, lineHeight:1.85, fontWeight:300, margin:0 }}>
        Options trading involves substantial risk of loss and is not
        suitable for all investors. The performance data shown reflects
        paper trading sessions — simulated trades with no real capital
        at risk. Real account performance will differ. Past performance
        does not guarantee future results. The Pattern Day Trader rule
        requires a minimum of $25,000 account equity. Never deploy
        capital you cannot afford to lose. BerBul does not provide
        financial advice.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME — consumer facing
// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const [mounted, setMounted] = useState(false);
  const [mkt, setMkt] = useState(getMarketStatus());

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    const i = setInterval(() => setMkt(getMarketStatus()), 30000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, []);

  const reveal = (d = 0) => ({
    opacity: mounted ? 1 : 0,
    animation: mounted ? `fadeUp .8s ${d}s ease both` : "none",
  });

  return (
    <Shell
      mkt={mkt}
      navLinks={[
        { href:"#proof", label:"Performance" },
        { href:"#who",   label:"Who It's For" },
        { href:"/investors", label:"Investors" },
      ]}
      ctaTo={mkt.isOpen ? "/Live-New" : "/trading"}
      ctaLabel={mkt.isOpen ? "Watch Live →" : "View Session →"}
    > 
      {/* ═══════════════════════════════════════════════════════════
          ZONE 1 — THESIS
      ═══════════════════════════════════════════════════════════ */}
      <ThesisSection />

      {/* ═══════════════════════════════════════════════════════════
          ZONE 1.5 — THE HOOK
      ═══════════════════════════════════════════════════════════ */}
      <section className="bb-hero">
        <div className="bb-glow" />
        <div className="bb-badge" style={reveal(.1)}>
          <Orb color={C.cyan} size={6} />
          AGENTIC AI · SPY 0DTE · FULLY AUTONOMOUS
        </div>
        <div className="bb-h1" style={reveal(.2)}>
          <span className="bb-h1-hook">The average trade is open for</span>
          <span className="bb-h1-num">97s</span>
          <span className="bb-h1-frame">
            Then the position closes. Capital recycles. Same day, every day.
          </span>
        </div>
        <p className="bb-sub" style={reveal(.38)}>
          BerBul deploys a <strong>fixed capital amount every morning</strong>,
          runs 10–12 precision trades on SPY 0DTE options,
          and closes everything by 4pm.{" "}
          <strong>Same capital. No overnight risk.
          Every decision explained in plain English.</strong>
        </p>
        <div className="bb-cta" style={reveal(.52)}>
          <Link className="bb-bm"
            to={mkt.isOpen ? "/Live-New" : "/trading"}>
            {mkt.isOpen ? "▶ Watch It Trade Live" : "▶ View Today's Session"}
          </Link>
          <Link className="bb-bg" to="/Login">Connect Your Broker</Link>
        </div>
        <div className="bb-caveat" style={reveal(.6)}>
          Based on recent paper trading sessions · fixed capital deployed daily ·
          see full session data below
        </div>
      </section>



      {/* ═══════════════════════════════════════════════════════════
          ZONE 2 — PROOF
      ═══════════════════════════════════════════════════════════ */}
      <div id="proof">
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <Eyebrow>Example Trade · Paper Account</Eyebrow>
          <Heading style={{ textAlign:"center", maxWidth:500, margin:"0 auto 16px" }}>
            This is what 97 seconds looks like.
          </Heading>
          <Body style={{ textAlign:"center", maxWidth:480, margin:"0 auto" }}>
            Entry decision, monitoring check, exit. Three events.
            One complete trade. The full reasoning behind each step
            is logged and readable after session close.
          </Body>
        </div>
        <TradeReplay />
        <div style={{ height:80 }} />
        <div className="bb-stats">
          <StatCard num="487"  prefix="$"  label="Avg Daily Return"
            sub="on fixed deployed capital" color={C.green}  delay={0}   />
          <StatCard num="78"   suffix="%"  label="Win Rate"
            sub="recent sessions"           color={C.cyan}   delay={.07} />
          <StatCard num="97"   suffix="s"  label="Avg Hold Time"
            sub="entry to exit"             color={C.purple} delay={.13} />
          <StatCard num="18"               label="Profitable Days"
            sub="of 22 trading days"        color={C.amber}  delay={.19} />
          <StatCard num="4"    prefix="<$" suffix="k" label="Max Live Exposure"
            sub="at any single moment"      color={C.text}   delay={.25} />
        </div>
        <div className="bb-caveat" style={{ marginBottom:72 }}>
          Paper account · SPY 0DTE · Feb 2026 ·
          past performance does not guarantee future results
        </div>
        <div className="bb-cumul">
          {[
            { label:"TOTAL RETURN SINCE LAUNCH", val:CUMULATIVE.totalReturn, color:C.green },
            { label:"TRADING SESSIONS",           val:CUMULATIVE.sessions,   color:C.cyan  },
            { label:"TRADING SINCE",              val:CUMULATIVE.startDate,  color:C.body  },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
                color:C.meta, letterSpacing:"1.2px", marginBottom:8 }}>{label}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
                fontSize:28, color, letterSpacing:"-.3px" }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ height:32 }} />
        <div style={{ marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"baseline",
            justifyContent:"space-between", flexWrap:"wrap", gap:8,
            marginBottom:4 }}>
            <Eyebrow>Example Session History</Eyebrow>
            <Chip color={C.amber}>PAPER TRADING · CURATED SAMPLE</Chip>
          </div>
          <Heading style={{ marginBottom:8 }}>
            Four sessions. Four market conditions.
          </Heading>
          <Body style={{ maxWidth:580, marginBottom:0 }}>
            Including a loss day and a quiet day — because the system's value
            is in how it handles every condition, not just the good ones.
          </Body>
        </div>
        <PerfTable />
      </div>

      <Divider />

      {/* ═══════════════════════════════════════════════════════════
          ZONE 3 — WHO IT'S FOR
      ═══════════════════════════════════════════════════════════ */}
      <section id="who">
        <div className="bb-shead">
          <Eyebrow>Who It's For</Eyebrow>
          <Heading>One platform. Three entry points.</Heading>
        </div>
        <div className="bb-segs">
          {SEGMENTS.map((s, i) => (
            <div key={i} className="bb-seg"
              style={{ borderTopColor:s.color + "40", borderTopWidth:2 }}>
              <div style={{ fontSize:20, color:s.color }}>{s.icon}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
                fontSize:15, color:C.text }}>{s.title}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
                color:C.text, lineHeight:1.7, fontWeight:500 }}>{s.hook}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
                color:C.body, lineHeight:1.9, fontWeight:300 }}>{s.desc}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
                color:C.meta, paddingTop:14, marginTop:"auto",
                borderTop:`1px solid ${C.border}`, letterSpacing:".5px" }}>
                {s.proof}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ═══════════════════════════════════════════════════════════
          ZONE 4 — CLOSE
      ═══════════════════════════════════════════════════════════ */}
      <section>
        <EmailCapture />
        <div style={{ height:72 }} />
        <section className="bb-bcta">
          <h2>Watch it trade live.</h2>
          <p>
            Open during market hours — no account required to observe.
            Full session history and P&L available after login.
          </p>
          <div className="bb-cta">
            <Link className="bb-bm"
              to={mkt.isOpen ? "/Live-New" : "/trading"}>
              {mkt.isOpen ? "▶ Open Live Terminal" : "▶ View Today's Session"}
            </Link>
            <Link className="bb-bg" to="/Login">Create Account</Link>
          </div>
          <div className="bb-demo">
            <Orb color={C.cyan} size={6} />
            <span>VC or institutional inquiry?</span>
            <Link to="/investors" style={{ color:C.cyan,
              textDecoration:"none", fontWeight:500 }}>
              View investor page →
            </Link>
          </div>
        </section>
        <RiskDisclosure />
      </section>

    </Shell>
  );
}